import React, { useState, useCallback, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import { auth } from '../services/api';
import { User } from '../types/user';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    code: '',
  });
  const [showCodeField, setShowCodeField] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { login, setUserData } = useAuth();
  const navigate = useNavigate();

  // Обратный отсчет для повторной отправки кода
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogin = useCallback(async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (loading) return;
    
    setError(null);
    setLoading(true);

    try {
      if (!showCodeField) {
        // Первый этап: проверяем пароль и отправляем код
        const response: any = await auth.login({ email: formData.email, password: formData.password });
        
        // Проверяем, требуется ли двухфакторная аутентификация
        if (response.data.requiresCode) {
          setShowCodeField(true);
          setCountdown(120); // 2 минуты
          toast.success('Код отправлен на ваш email');
        } else {
          // Для админа или если токен получен сразу
          if (response.data.token) {
            sessionStorage.setItem('token', response.data.token);
            sessionStorage.setItem('refreshToken', response.data.refreshToken);
            
            // Обновляем состояние пользователя в AuthContext
            if (response.data.user) {
              setUserData(response.data.user as unknown as User);
            } else {
              // Если пользователь не в ответе, получаем профиль
              try {
                const profileResponse = await auth.getProfile();
                setUserData(profileResponse.data as unknown as User);
              } catch (profileError) {
                console.error('Ошибка при получении профиля:', profileError);
              }
            }
            
            toast.success('Вход выполнен успешно');
            navigate('/');
          }
        }
      } else {
        // Второй этап: проверяем код
        if (!formData.code.trim()) {
          setError('Введите код');
          return;
        }
        
        const response = await auth.loginWithCode({ 
          email: formData.email, 
          code: formData.code 
        });
        
        // Сохраняем токены
        sessionStorage.setItem('token', response.data.token);
        sessionStorage.setItem('refreshToken', response.data.refreshToken);
        
        // Обновляем состояние пользователя в AuthContext
        if (response.data.user) {
          setUserData(response.data.user as unknown as User);
          navigate('/');
        } else {
          // Если пользователь не в ответе, получаем профиль
          try {
            const profileResponse = await auth.getProfile();
            setUserData(profileResponse.data as unknown as User);
            navigate('/');
          } catch (profileError) {
            console.error('Ошибка при получении профиля:', profileError);
            navigate('/');
          }
        }
        
        toast.success('Вход выполнен успешно');
      }
    } catch (error: any) {
      console.error('Ошибка при входе:', error);
      const errorMessage = error.response?.data?.message || 'Ошибка при входе в систему';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [formData, loading, navigate, showCodeField]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleLogin();
    }
  }, [handleLogin]);

  const handleResendCode = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    setError(null);

    try {
      await auth.sendLoginCode({ email: formData.email });
      setCountdown(120);
      toast.success('Новый код отправлен');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Ошибка при отправке кода';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Вход в систему
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            autoComplete="email"
            autoFocus
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Пароль"
            type="password"
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            disabled={loading}
          />
          
          {showCodeField && (
            <>
              <Typography variant="body1" sx={{ mt: 2, mb: 1, textAlign: 'center' }}>
                Код отправлен на <strong>{formData.email}</strong>
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                id="code"
                label="Код из письма"
                name="code"
                autoComplete="off"
                autoFocus
                value={formData.code}
                onChange={handleChange}
                disabled={loading}
                inputProps={{ maxLength: 6 }}
              />
              <Button
                variant="text"
                fullWidth
                onClick={handleResendCode}
                disabled={loading || countdown > 0}
                sx={{ mt: 1, textTransform: 'none' }}
              >
                {countdown > 0 ? `Отправить код повторно (${countdown}s)` : 'Отправить код повторно'}
              </Button>
            </>
          )}
          
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : (showCodeField ? 'Завершить вход' : 'Войти')}
          </Button>
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link component={RouterLink} to="/register" variant="body2">
              Нет аккаунта? Зарегистрируйтесь
            </Link>
          </Box>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              variant="body2"
              sx={{ textDecoration: 'none' }}
            >
              Забыли пароль?
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login; 